import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Boxes, Link2, Repeat, Store } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { PaginatedDataTable, type DataColumn } from '@/components/products/PaginatedDataTable';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import {
  fetchProductAffinityExport,
  fetchProductRepeatExport,
  fetchProductVelocityExport,
  useProductAffinity,
  useProductRepeat,
  useProductStores,
  useProductVelocity,
} from '@/api/bi';
import type {
  ProductAffinityPair,
  ProductRepeatSkuRow,
  ProductTaxonomyFilters,
  ProductVelocityRow,
} from '@/api/bi';
import type { ProductScope } from '@/pages/products/useProductScope';

const PAGE_SIZE = 20;

/** Shared period + taxonomy slice every section feeds to its hook/export. */
type ScopeBase = ProductTaxonomyFilters & { start: string; end: string };

interface Props {
  scope: ProductScope;
}

/**
 * Stage 14 merchandising add-ons mounted on the Products page: repeat-customer
 * behaviour, store-wise comparison, basket affinity and sell-through velocity.
 * The list tables are server-paginated (20/page) and export the full dataset
 * to CSV via the `?export=1` endpoints. Everything is wired to the shared
 * taxonomy/period/store scope so the global rail filters them in lockstep.
 */
export function MerchandisingInsights({ scope }: Props) {
  const base = useMemo<ScopeBase>(
    () => ({ start: scope.iso.start, end: scope.iso.end, ...scope.taxonomy }),
    [scope.iso.start, scope.iso.end, scope.taxonomy],
  );

  const compareStart = scope.compareEnabled ? scope.compareRange?.start : undefined;
  const compareEnd = scope.compareEnabled ? scope.compareRange?.end : undefined;

  return (
    <>
      <RepeatSection base={base} />
      <StoreSection
        base={base}
        compareStart={compareStart}
        compareEnd={compareEnd}
        compared={scope.compareEnabled}
      />
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <AffinitySection base={base} />
        <VelocitySection base={base} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Re-orders / repeat customers
// ---------------------------------------------------------------------------

function RepeatSection({ base }: { base: ScopeBase }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [base]);

  const query = useProductRepeat({ ...base, page, pageSize: PAGE_SIZE });
  const data = query.data;
  const ov = data?.overview;
  const distData = useMemo(
    () => (data?.distribution ?? []).map((d) => ({ bucket: d.bucket, customers: d.customers })),
    [data],
  );

  return (
    <div className="mt-4">
      <Card>
        <CardHeader>
          <CardTitle>Re-orders & repeat customers</CardTitle>
          <Badge tone="info" className="gap-1">
            <Repeat className="h-3 w-3" /> Loyalty
          </Badge>
        </CardHeader>
        <CardBody>
          {query.isError ? (
            <ErrorState onRetry={() => void query.refetch()} />
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KPICard
                  label="Reorder rate"
                  value={ov ? fmt.pct(ov.reorder_rate) : '—'}
                  loading={query.isLoading}
                  hint="Customers with 2+ completed orders for the scoped products ÷ all customers in range."
                />
                <KPICard
                  label="Returning GMV share"
                  value={ov ? fmt.pct(ov.returning_gmv_share) : '—'}
                  loading={query.isLoading}
                  hint="Share of product GMV from customers who re-ordered."
                />
                <KPICard
                  label="Avg days to reorder"
                  value={
                    data?.cadence.avg_days_between != null
                      ? `${fmt.number(data.cadence.avg_days_between)}d`
                      : '—'
                  }
                  loading={query.isLoading}
                  hint="Mean gap between consecutive orders for returning customers."
                />
                <KPICard
                  label="Returning customers"
                  value={ov ? fmt.compact(ov.returning_customers) : '—'}
                  loading={query.isLoading}
                  hint={
                    ov ? `${fmt.compact(ov.new_customers)} new · ${fmt.compact(ov.total_customers)} total` : undefined
                  }
                />
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Orders per customer
                  </p>
                  {query.isLoading ? (
                    <Skeleton className="h-[220px] w-full" />
                  ) : distData.every((d) => d.customers === 0) ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">No data in range.</p>
                  ) : (
                    <BarChart
                      data={distData}
                      xKey="bucket"
                      yKey="customers"
                      height={220}
                      formatValue={(v) => fmt.compact(v)}
                    />
                  )}
                </div>
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Most re-ordered SKUs
                  </p>
                  <PaginatedDataTable
                    data={data?.top_reordered ?? []}
                    total={data?.top_reordered_total ?? 0}
                    page={page}
                    onPage={setPage}
                    pageSize={PAGE_SIZE}
                    loading={query.isLoading}
                    exportName="most_reordered_skus"
                    columns={REORDER_COLUMNS}
                    onDownloadAll={async () => (await fetchProductRepeatExport(base)).top_reordered}
                  />
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Store-wise comparative
// ---------------------------------------------------------------------------

function StoreSection({
  base,
  compareStart,
  compareEnd,
  compared,
}: {
  base: ScopeBase;
  compareStart?: string;
  compareEnd?: string;
  compared: boolean;
}) {
  const query = useProductStores({ ...base, compareStart, compareEnd });
  const rows = query.data?.rows ?? [];
  const chartData = useMemo(
    () => rows.slice(0, 12).map((r) => ({ store: r.store_name, gmv: r.gmv })),
    [rows],
  );

  return (
    <div className="mt-4">
      <Card>
        <CardHeader>
          <CardTitle>Store-wise performance</CardTitle>
          <Badge tone="neutral" className="gap-1">
            <Store className="h-3 w-3" /> Comparative
          </Badge>
        </CardHeader>
        <CardBody>
          {query.isError ? (
            <ErrorState onRetry={() => void query.refetch()} />
          ) : query.isLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No store sales in range.</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <BarChart
                data={chartData}
                xKey="store"
                yKey="gmv"
                height={300}
                horizontal
                formatValue={(v) => fmt.npr(v)}
              />
              <SimpleTable
                head={['Store', 'GMV', 'Share', 'Margin', 'AOV', compared ? 'Δ GMV' : 'Orders']}
                align={['left', 'right', 'right', 'right', 'right', 'right']}
                rows={rows.map((r) => [
                  r.store_name,
                  fmt.npr(r.gmv),
                  fmt.pct(r.contribution_pct),
                  r.margin_pct != null ? fmt.pct(r.margin_pct) : '—',
                  fmt.npr(r.aov),
                  compared
                    ? r.gmv_delta_pct != null
                      ? deltaCell(r.gmv_delta_pct)
                      : '—'
                    : fmt.number(r.orders),
                ])}
              />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Basket affinity
// ---------------------------------------------------------------------------

function AffinitySection({ base }: { base: ScopeBase }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [base]);

  const query = useProductAffinity({ ...base, page, pageSize: PAGE_SIZE });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Frequently bought together</CardTitle>
        <Badge tone="neutral" className="gap-1">
          <Link2 className="h-3 w-3" /> Cross-sell
        </Badge>
      </CardHeader>
      <CardBody>
        {query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : (
          <PaginatedDataTable
            data={query.data?.pairs ?? []}
            total={query.data?.pairs_total ?? 0}
            page={page}
            onPage={setPage}
            pageSize={PAGE_SIZE}
            loading={query.isLoading}
            exportName="frequently_bought_together"
            columns={AFFINITY_COLUMNS}
            emptyLabel="No co-purchased pairs in range."
            onDownloadAll={async () => (await fetchProductAffinityExport(base)).pairs}
          />
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Velocity / dead-stock
// ---------------------------------------------------------------------------

function VelocitySection({ base }: { base: ScopeBase }) {
  const [fastPage, setFastPage] = useState(1);
  const [deadPage, setDeadPage] = useState(1);
  useEffect(() => {
    setFastPage(1);
    setDeadPage(1);
  }, [base]);

  const query = useProductVelocity({ ...base, fastPage, deadPage, pageSize: PAGE_SIZE });
  const data = query.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sell-through velocity</CardTitle>
        <Badge tone="neutral" className="gap-1">
          <Boxes className="h-3 w-3" /> Stock-aware
        </Badge>
      </CardHeader>
      <CardBody>
        {query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 font-medium text-success">
                {fmt.number(data?.summary.fast_movers ?? 0)} fast movers
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-danger/30 bg-danger/10 px-2 py-0.5 font-medium text-danger">
                {fmt.number(data?.summary.dead_stock ?? 0)} dead stock
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 font-medium text-muted-foreground">
                {fmt.compact(data?.summary.stock_units ?? 0)} units on hand
              </span>
            </div>

            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Fast movers (low days of cover)
            </p>
            <PaginatedDataTable
              data={data?.fast_movers ?? []}
              total={data?.fast_movers_total ?? 0}
              page={fastPage}
              onPage={setFastPage}
              pageSize={PAGE_SIZE}
              loading={query.isLoading}
              exportName="fast_movers"
              columns={FAST_MOVER_COLUMNS}
              onDownloadAll={async () => (await fetchProductVelocityExport(base)).fast_movers}
            />

            <p className="mb-1.5 mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Dead stock (on-hand, no sales)
            </p>
            <PaginatedDataTable
              data={data?.dead_stock ?? []}
              total={data?.dead_stock_total ?? 0}
              page={deadPage}
              onPage={setDeadPage}
              pageSize={PAGE_SIZE}
              loading={query.isLoading}
              exportName="dead_stock"
              columns={DEAD_STOCK_COLUMNS}
              onDownloadAll={async () => (await fetchProductVelocityExport(base)).dead_stock}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tiny shared table (store comparison only)
// ---------------------------------------------------------------------------

type Align = 'left' | 'right';

function SimpleTable({
  head,
  rows,
  align,
  empty,
}: {
  head: string[];
  rows: ReactNode[][];
  align?: Align[];
  empty?: boolean;
}) {
  if (empty || rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No data in range.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
            {head.map((h, i) => (
              <th
                key={h}
                className={cn('px-2 py-1.5 font-semibold', align?.[i] === 'right' ? 'text-right' : 'text-left')}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, ri) => (
            <tr key={ri} className="border-b border-border/50 last:border-0">
              {cells.map((c, ci) => (
                <td
                  key={ci}
                  className={cn(
                    'px-2 py-1.5',
                    align?.[ci] === 'right' ? 'kpi-num text-right tabular-nums' : 'text-foreground',
                    ci === 0 ? 'max-w-[16rem] truncate' : '',
                  )}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function deltaCell(pct: number) {
  const positive = pct > 0;
  return (
    <span className={cn('font-medium', positive ? 'text-success' : 'text-danger')}>
      {fmt.delta(pct, { unit: 'pct' })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Column definitions (shared by on-screen render + full-data CSV export)
// ---------------------------------------------------------------------------

const REORDER_COLUMNS: DataColumn<ProductRepeatSkuRow>[] = [
  { header: 'Product', align: 'left', cell: (r) => r.name, csv: (r) => r.name },
  { header: 'Orders', align: 'right', cell: (r) => fmt.number(r.orders), csv: (r) => r.orders },
  { header: 'Customers', align: 'right', cell: (r) => fmt.number(r.customers), csv: (r) => r.customers },
  { header: 'Re-orders', align: 'right', cell: (r) => fmt.number(r.repeat_orders), csv: (r) => r.repeat_orders },
  { header: 'Reorder %', align: 'right', cell: (r) => fmt.pct(r.reorder_rate), csv: (r) => r.reorder_rate },
  { header: 'Units', align: 'right', cell: (r) => fmt.number(r.units), csv: (r) => r.units },
  { header: 'GMV', align: 'right', cell: (r) => fmt.npr(r.gmv), csv: (r) => r.gmv },
];

const AFFINITY_COLUMNS: DataColumn<ProductAffinityPair>[] = [
  { header: 'Product A', align: 'left', cell: (p) => p.a_name, csv: (p) => p.a_name },
  { header: 'Product B', align: 'left', cell: (p) => p.b_name, csv: (p) => p.b_name },
  { header: 'Orders together', align: 'right', cell: (p) => fmt.number(p.orders_together), csv: (p) => p.orders_together },
  { header: 'Lift', align: 'right', cell: (p) => `${fmt.number(p.lift)}×`, csv: (p) => p.lift },
];

const FAST_MOVER_COLUMNS: DataColumn<ProductVelocityRow>[] = [
  { header: 'Product', align: 'left', cell: (r) => r.name, csv: (r) => r.name },
  { header: 'Units/day', align: 'right', cell: (r) => fmt.number(r.velocity_per_day), csv: (r) => r.velocity_per_day },
  { header: 'Units sold', align: 'right', cell: (r) => fmt.number(r.units), csv: (r) => r.units },
  { header: 'Stock', align: 'right', cell: (r) => fmt.number(r.stock), csv: (r) => r.stock },
  {
    header: 'Cover',
    align: 'right',
    cell: (r) => (r.days_of_cover != null ? `${fmt.number(r.days_of_cover)}d` : '—'),
    csv: (r) => r.days_of_cover,
  },
];

const DEAD_STOCK_COLUMNS: DataColumn<ProductVelocityRow>[] = [
  { header: 'Product', align: 'left', cell: (r) => r.name, csv: (r) => r.name },
  { header: 'Stock', align: 'right', cell: (r) => fmt.number(r.stock), csv: (r) => r.stock },
  { header: 'GMV', align: 'right', cell: (r) => fmt.npr(r.gmv), csv: (r) => r.gmv },
];
